import { addDays, addMonths, differenceInCalendarDays, eachDayOfInterval, eachMonthOfInterval, endOfMonth, format, startOfDay, startOfMonth, startOfWeek, startOfYear, subMonths, subWeeks, subYears } from 'date-fns';
import { it } from 'date-fns/locale';
import { getCurrentRomeTime } from '../../utils/formatters';

const safeParseFloat = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'number') return val;
  const str = String(val).replace(',', '.');
  const num = parseFloat(str);
  return Number.isFinite(num) ? num : 0;
};

// Helper per la precisione
export const toFixedNumber = (num) => Math.round(num * 1e2) / 1e2;

// Totale spese e entrate
export function getTotal(expenses, incomes) {
  const totalExpenses = (expenses || [])
    .filter(e => e && e.category !== 'Trasferimento')
    .reduce((sum, e) => sum + safeParseFloat(e.amount), 0);

  const totalIncomes = (incomes || [])
    .filter(i => i && i.category !== 'Trasferimento')
    .reduce((sum, i) => sum + safeParseFloat(i.amount), 0);

  return {
    totalExpenses: toFixedNumber(totalExpenses),
    totalIncomes: toFixedNumber(totalIncomes),
    balance: toFixedNumber(totalIncomes - totalExpenses)
  };
}

// Statistiche per categoria
export function getByCategory(items) {
  return items.filter(e => e && typeof e === 'object' && e.category !== 'Trasferimento' && e.category !== undefined)
    .reduce((acc, e) => {
      acc[e.category] = (acc[e.category] || 0) + parseFloat(e.amount);
      return acc;
    }, {});
}

// Statistiche per mese (ritorna array ordinato)
export function getMonthlyData(expenses, incomes) {
  const months = {};

  const addItem = (item, key) => {
    if (!item || item.category === 'Trasferimento') {
      return;
    }

    const date = new Date(item.date);
    if (Number.isNaN(date.getTime())) {
      return;
    }

    const amount = Number.parseFloat(item.amount);
    if (!Number.isFinite(amount)) {
      return;
    }

    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!months[monthKey]) months[monthKey] = { month: monthKey, Spese: 0, Entrate: 0 };
    months[monthKey][key] += amount;
  };

  expenses.forEach(item => addItem(item, 'Spese'));
  incomes.forEach(item => addItem(item, 'Entrate'));

  return Object.values(months).sort((a, b) => a.month.localeCompare(b.month));
}

// Statistiche per negozio
export function getByStore(items) {
  return items.filter(e => e && typeof e === 'object' && e.category !== 'Trasferimento')
    .reduce((acc, e) => {
      const store = e.store || 'Senza negozio';
      acc[store] = (acc[store] || 0) + parseFloat(e.amount);
      return acc;
    }, {});
}

// Bilancio mensile ultimi N mesi
export function getMonthlyBalance(expenses, incomes, monthsArr) {
  const map = {};
  expenses.filter(e => e.category !== 'Trasferimento').forEach(e => {
    const d = new Date(e.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!map[key]) map[key] = { entrate: 0, spese: 0 };
    map[key].spese += parseFloat(e.amount);
  });
  incomes.filter(i => i.category !== 'Trasferimento').forEach(i => {
    const d = new Date(i.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!map[key]) map[key] = { entrate: 0, spese: 0 };
    map[key].entrate += parseFloat(i.amount);
  });
  return monthsArr.map(m => {
    const b = map[m] || { entrate: 0, spese: 0 };
    return { mese: m, bilancio: b.entrate - b.spese };
  });
}

// Heatmap giornaliera media (array 7 giorni, Lun-Dom) per spese e entrate
export function getDailyAvgHeatmap(expenses = [], incomes = []) {
  const buildDailyAverage = (items = []) => {
    const sums = Array(7).fill(0);
    const counts = Array(7).fill(0);

    items
      .filter(item => item && typeof item === 'object' && item.category !== 'Trasferimento')
      .forEach(item => {
        const date = new Date(item.date);
        if (Number.isNaN(date.getTime())) {
          return;
        }

        const amount = Number.parseFloat(item.amount);
        if (!Number.isFinite(amount)) {
          return;
        }

        const dayIndex = (date.getDay() + 6) % 7;
        sums[dayIndex] += amount;
        counts[dayIndex] += 1;
      });

    const averages = sums.map((sum, idx) => (counts[idx] ? sum / counts[idx] : 0));
    const daysWithData = counts.filter(Boolean).length || 0;
    const overallAverage = daysWithData ? averages.reduce((acc, value) => acc + value, 0) / daysWithData : 0;

    return { averages, overallAverage };
  };

  const expenseAverages = buildDailyAverage(expenses);
  const incomeAverages = buildDailyAverage(incomes);

  return {
    expenses: expenseAverages.averages,
    incomes: incomeAverages.averages,
    expensesMean: expenseAverages.overallAverage,
    incomesMean: incomeAverages.overallAverage,
  };
}

// Trend giornaliero per il grafico
export function getDailyTrend(expenses, incomes, start, end) {
  if (!start || !end) return [];

  // Genera tutti i giorni nell'intervallo
  const allDays = eachDayOfInterval({
    start,
    end: new Date(end.getTime() - 1), // Sottrai 1ms per non includere l'inizio del periodo successivo se esatto
  });

  const buckets = {};

  // Inizializza tutti i giorni con valore 0
  allDays.forEach(day => {
    const label = format(day, "d LLL", { locale: it });
    const key = format(day, 'yyyy-MM-dd');
    buckets[key] = {
      label,
      Entrate: 0,
      Spese: 0,
      order: day.getTime()
    };
  });

  const pushItem = (item, typeKey) => {
    if (!item?.date || item.category === 'Trasferimento') return;
    const date = new Date(item.date);
    if (Number.isNaN(date.getTime())) return;
    if (date < start || date >= end) return;

    const key = format(date, 'yyyy-MM-dd');
    if (buckets[key]) {
      buckets[key][typeKey] += Number.parseFloat(item.amount) || 0;
    }
  };

  (incomes || []).forEach((item) => pushItem(item, 'Entrate'));
  (expenses || []).forEach((item) => pushItem(item, 'Spese'));

  return Object.values(buckets).sort((a, b) => a.order - b.order);
}

// Confronto bilancio (periodo selezionato contro periodo precedente)
export function getBalanceComparison(expenses, incomes, range = 'week', metric = 'balance', anchorDate, options = {}) {
  const cleanExpenses = (expenses || []).filter(item => item && item.category !== 'Trasferimento' && item.date);
  const cleanIncomes = (incomes || []).filter(item => item && item.category !== 'Trasferimento' && item.date);

  const mapByDayBalance = {};
  const mapByDayIncome = {};
  const mapByDayExpense = {};
  const mapByMonthBalance = {};
  const mapByMonthIncome = {};
  const mapByMonthExpense = {};

  const pushValue = (map, key, value) => {
    map[key] = (map[key] || 0) + value;
  };

  cleanIncomes.forEach(item => {
    const amount = Number.parseFloat(item.amount);
    if (!Number.isFinite(amount)) return;
    const date = new Date(item.date);
    if (Number.isNaN(date.getTime())) return;
    const dayKey = format(date, 'yyyy-MM-dd');
    const monthKey = format(date, 'yyyy-MM');
    pushValue(mapByDayIncome, dayKey, amount);
    pushValue(mapByMonthIncome, monthKey, amount);
    pushValue(mapByDayBalance, dayKey, amount);
    pushValue(mapByMonthBalance, monthKey, amount);
  });

  cleanExpenses.forEach(item => {
    const amount = Number.parseFloat(item.amount);
    if (!Number.isFinite(amount)) return;
    const date = new Date(item.date);
    if (Number.isNaN(date.getTime())) return;
    const dayKey = format(date, 'yyyy-MM-dd');
    const monthKey = format(date, 'yyyy-MM');
    pushValue(mapByDayExpense, dayKey, amount);
    pushValue(mapByMonthExpense, monthKey, amount);
    pushValue(mapByDayBalance, dayKey, -amount);
    pushValue(mapByMonthBalance, monthKey, -amount);
  });

  const normalizedMetric = (() => {
    const lower = (metric || 'balance').toLowerCase();
    if (lower === 'entrate' || lower === 'income') return 'income';
    if (lower === 'spese' || lower === 'expense' || lower === 'expenses') return 'expense';
    return 'balance';
  })();

  const metricMaps = {
    balance: { daily: mapByDayBalance, monthly: mapByMonthBalance },
    income: { daily: mapByDayIncome, monthly: mapByMonthIncome },
    expense: { daily: mapByDayExpense, monthly: mapByMonthExpense },
  };

  const selectedMaps = metricMaps[normalizedMetric] || metricMaps.balance;
  const periodAnchor = startOfDay(anchorDate || new Date());
  const realNow = startOfDay(new Date());

  if (range === 'custom' && options.customDates) {
    const { currentStart, currentEnd, previousStart, previousEnd } = options.customDates;
    const durationDays = Math.ceil((currentEnd.getTime() - currentStart.getTime()) / (24 * 60 * 60 * 1000));
    
    const currentSeries = buildDailySeries(selectedMaps.daily, currentStart, durationDays, realNow, 'custom');
    const previousSeries = buildDailySeries(selectedMaps.daily, previousStart, durationDays, undefined, 'custom');
    
    return mergeSeries(currentSeries, previousSeries);
  }

  if (range === 'year') {
    const months = 12;
    const currentStart = startOfYear(periodAnchor);
    const previousStart = startOfYear(subYears(currentStart, 1));

    const currentSeries = buildMonthlySeries(selectedMaps.monthly, currentStart, months, realNow);
    const previousSeries = buildMonthlySeries(selectedMaps.monthly, previousStart, months);

    return mergeSeries(currentSeries, previousSeries);
  }

  if (range === 'month') {
    const currentStart = startOfMonth(periodAnchor);
    const previousStart = startOfMonth(subMonths(currentStart, 1));
    const currentMonthDays = differenceInCalendarDays(endOfMonth(currentStart), currentStart) + 1;
    const previousMonthDays = differenceInCalendarDays(endOfMonth(previousStart), previousStart) + 1;
    const totalDays = 31;

    const currentSeries = buildDailySeries(
      selectedMaps.daily,
      currentStart,
      totalDays,
      realNow,
      'month',
      { actualDays: currentMonthDays, forceMonthDayLabels: true }
    );
    const previousSeries = buildDailySeries(
      selectedMaps.daily,
      previousStart,
      totalDays,
      undefined,
      'month',
      { actualDays: previousMonthDays, forceMonthDayLabels: true }
    );

    return mergeSeries(currentSeries, previousSeries);
  }

  const currentStart = startOfWeek(periodAnchor, { weekStartsOn: 1 });
  const previousStart = startOfWeek(subWeeks(currentStart, 1), { weekStartsOn: 1 });
  const daysInWeek = 7;

  const currentSeries = buildDailySeries(selectedMaps.daily, currentStart, daysInWeek, realNow, 'week');
  const previousSeries = buildDailySeries(selectedMaps.daily, previousStart, daysInWeek, undefined, 'week');

  return mergeSeries(currentSeries, previousSeries);
}

function buildDailySeries(mapByDay, start, totalDays, clampEnd, mode = 'default', options = {}) {
  const {
    actualDays = totalDays,
    forceMonthDayLabels = false,
  } = options;
  const labels = [];
  const values = [];
  let cursor = start;
  let running = 0;
  for (let i = 0; i < totalDays; i++) {
    const useActualDate = i < actualDays;
    const currentDate = useActualDate ? cursor : null;
    const dayKey = currentDate ? format(currentDate, 'yyyy-MM-dd') : null;
    const isFuture = (clampEnd && currentDate && currentDate > clampEnd) || !useActualDate;
    if (!isFuture && dayKey) {
      running += mapByDay[dayKey] || 0;
    }
    let rawLabel;
    if (mode === 'week') {
      rawLabel = format(currentDate || cursor, 'EEE', { locale: it });
      rawLabel = capitalize(rawLabel);
    } else if (mode === 'month') {
      if (forceMonthDayLabels) {
        rawLabel = String(i + 1);
      } else {
        rawLabel = format(currentDate || cursor, 'd', { locale: it });
      }
    } else {
      rawLabel = format(currentDate || cursor, 'dd MMM', { locale: it });
    }
    labels.push(rawLabel);
    values.push(isFuture ? null : running);
    if (useActualDate) {
      cursor = addDays(cursor, 1);
    }
  }
  return { labels, values };
}

function buildMonthlySeries(mapByMonth, start, months, clampDate) {
  const labels = [];
  const values = [];
  let cursor = startOfMonth(start);
  let running = 0;
  for (let i = 0; i < months; i++) {
    const monthKey = format(cursor, 'yyyy-MM');
    const monthEnd = endOfMonth(cursor);
    const isFuture = clampDate && monthEnd > clampDate;
    if (!isFuture) {
      running += mapByMonth[monthKey] || 0;
    }
    labels.push(format(cursor, "MMM ''yy", { locale: it }));
    values.push(isFuture ? null : running);
    cursor = addMonths(cursor, 1);
  }
  return { labels, values };
}

function mergeSeries(currentSeries, previousSeries) {
  return currentSeries.labels.map((label, idx) => ({
    label,
    current: currentSeries.values[idx],
    previous: previousSeries.values[idx] ?? null,
  }));
}

function capitalize(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Calcola l'andamento del patrimonio partendo dal saldo attuale dei conti.
export function getNetWorthTrend(expenses, incomes, debts, wallets, start, end) {
  if (!start || !end) return [];

  const now = getCurrentRomeTime();
  const today = startOfDay(now);
  const currentNetWorth = (wallets || [])
    .filter(wallet => !wallet?.isVirtual)
    .reduce((sum, wallet) => sum + safeParseFloat(wallet?.balance), 0);

  const deltasByDay = {};
  const walletIdSet = new Set(
    (wallets || [])
      .filter(wallet => !wallet?.isVirtual)
      .map(wallet => wallet?.id)
      .filter(Boolean)
  );

  const addDelta = (dateObj, delta) => {
    const dayKey = format(dateObj, 'yyyy-MM-dd');
    deltasByDay[dayKey] = (deltasByDay[dayKey] || 0) + delta;
  };

  const addDirectTransaction = (item, sign) => {
    if (!item || typeof item !== 'object') return;
    if (!walletIdSet.has(item.walletId)) return;

    const amount = safeParseFloat(item.amount);
    if (!Number.isFinite(amount) || amount === 0) return;

    if (!item?.date) return;
    const dateObj = new Date(item.date);
    if (Number.isNaN(dateObj.getTime())) return;
    if (dateObj > now) return;

    addDelta(dateObj, sign * amount);
  };

  (incomes || []).forEach(item => addDirectTransaction(item, 1));
  (expenses || []).forEach(item => addDirectTransaction(item, -1));

  (debts || []).forEach((debt) => {
    if (!debt || typeof debt !== 'object') return;
    if (!walletIdSet.has(debt.walletId)) return;

    const amount = safeParseFloat(debt.amount);
    if (!Number.isFinite(amount) || amount === 0) return;

    const isCredit = debt.type === 'credit';
    const delta = isCredit ? -amount : amount;
    if (!debt?.date) return;
    const dateObj = new Date(debt.date);
    if (Number.isNaN(dateObj.getTime())) return;
    if (dateObj > now) return;

    addDelta(dateObj, delta);
  });

  // Crea un array di giorni nell'intervallo
  const days = eachDayOfInterval({ start, end });
  let runningBalance = currentNetWorth;
  const results = [];
  for (let i = days.length - 1; i >= 0; i--) {
    const dayStart = startOfDay(days[i]);
    const dayKey = format(dayStart, 'yyyy-MM-dd');
    const isFuture = dayStart > today;

    // Se stiamo guardando un periodo passato (end < today), dobbiamo prima "riavvolgere"
    // il runningBalance sottraendo tutti i delta che sono avvenuti tra la fine del periodo e oggi.
    if (i === days.length - 1 && dayStart < today) {
      // Calcola tutti i delta avvenuti DOPO la fine di questo intervallo fino a oggi
      Object.entries(deltasByDay).forEach(([dateKey, delta]) => {
        if (dateKey > dayKey) {
          runningBalance -= delta;
        }
      });
    }

    results[i] = {
      date: dayKey,
      label: format(dayStart, 'd MMM', { locale: it }),
      value: isFuture ? null : toFixedNumber(runningBalance)
    };
    if (!isFuture && deltasByDay[dayKey]) {
      runningBalance -= deltasByDay[dayKey];
    }
  }

  return results;
}

// Calcola la suddivisione tra dirette e debiti/crediti
// Calcola la suddivisione tra dirette e debiti/crediti
export function getBreakdown(transactions, ignoredDebts, type) {
  // transactions = array unico di expenses o incomes
  const cleanTransactions = (transactions || []).filter(t => t && t.category !== 'Trasferimento');

  let direct = 0;
  let debtRelated = 0;

  cleanTransactions.forEach(t => {
    const amount = safeParseFloat(t.amount);

    // Strategia robusta: 
    // 1. Se ha un "personName" O è di tipo "debt"/"credit", è legato a debiti/crediti
    // 2. Se ha un "walletId" (e non è debito), è diretto

    const isDebtType = t.type === 'debt' || t.type === 'credit';
    const hasPerson = !!t.personName;
    const isLinkedDebt = !!t.debtPersonName || !!t.linkedDebtId || t.isDebtTransaction === true;

    if (isDebtType || hasPerson || isLinkedDebt) {
      debtRelated += amount;
    } else if (t.walletId) {
      direct += amount;
    }
    // Se non ha né personName né walletId, potrebbe essere un dato spurio, lo ignoriamo 
    // o lo contiamo come diretto se vogliamo essere permissivi. 
    // L'utente ha chiesto: "assicurati che siano collegate ad un portafoglio o a una persona"
    // Quindi se manca tutto, non lo sommiamo a nessuno dei due bucket specifici, 
    // ma contribuisce al totale implicitamente? 
    // No, lo split deve dare il totale. 
    // Se è orfano, lo mettiamo in direct come fallback o lo ignoriamo?
    // Meglio contarlo in direct per non perdere cifre, ma la logica sopra è esplicita.
    else {
      direct += amount; // Fallback per vecchie transazioni senza walletId esplicito?
    }
  });

  const total = direct + debtRelated;

  return {
    total: toFixedNumber(total),
    direct: toFixedNumber(direct),
    debtRelated: toFixedNumber(debtRelated)
  };
}

export function filterItems(items, filters) {
  if (!items || !Array.isArray(items)) return [];
  if (!filters) return items;

  const {
    selectedCategories = [],
    selectedStores = [],
    selectedWallets = [],
    transactionType = 'all',
    showTransfers = false
  } = filters;

  return items.filter(item => {
    if (!item) return false;

    // 1. Tipo di transazione (filtro globale Uscite vs Entrate)
    // Nota: Gli elementi possono provenire dalle collezioni 'expenses' o 'incomes'.
    // Usa item.type se disponibile, o il contesto. 
    // Tuttavia, il componente Statistics passa array specifici. 
    // Se stiamo filtrando l'array "incomes" ma il filtro globale dice "expenses", dovremmo probabilmente restituire vuoto?
    // Assumiamo che gli elementi abbiano una proprietà 'type' O ci affidiamo al componente per chiamare questa funzione con il contesto appropriato.
    // In realtà, le collezioni expenses/incomes di solito non hanno il 'type' memorizzato in Firestore se sono in collezioni separate,
    // ma potrebbero averlo iniettato.
    // Controlliamo App.jsx... dati normalizzati?
    // In App.jsx: `const { data: expenses } = useCollectionData('expenses');`
    // Sono dati grezzi.
    // MA, guardiamo l'implementazione di filterItems.
    // Se vogliamo supportare il filtro transactionType, dobbiamo conoscere il tipo dell'elemento.
    // O applichiamo semplicemente i filtri categoria/negozio/portafoglio. 
    // Se l'utente seleziona "Uscite" nei filtri, probabilmente si aspetta che l'intera vista si focalizzi sulle uscite.
    // La logica delle statistiche chiama getTotal(expenses, incomes).
    // Dovremmo filtrare gli array di input PRIMA di passarli alla logica.
    // Se transactionType è 'expenses', l'array incomes dovrebbe essere vuoto dopo il filtraggio.
    // Il modo più sicuro è controllare se l'elemento appartiene effettivamente al tipo escluso.
    // Ma poiché non conosciamo il tipo con certezza dal solo oggetto (a meno che non lo iniettiamo), 
    // forse è meglio NON filtrare per tipo qui, O assumere che il chiamante (Statistics.jsx) gestisca la logica dell' "array vuoto"?
    // NO, l'utente ha detto "le statistiche si basano su quelli".
    // Se seleziono "Categoria: Cibo" (che è un'uscita), le Entrate dovrebbero essere vuote perché nessuna entrata ha la categoria Cibo?

    // Filtro Categoria
    if (selectedCategories.length > 0) {
      if (!selectedCategories.includes(item.category)) {
        return false;
      }
    }

    // Store Filter
    if (selectedStores.length > 0) {
      const storeName = item.store || '';
      if (!selectedStores.includes(storeName)) {
        return false;
      }
    }

    // Wallet Filter
    if (selectedWallets.length > 0) {
      // L'id del portafoglio potrebbe essere null per vecchi elementi o contanti?
      if (!selectedWallets.includes(item.walletId)) {
        return false;
      }
    }

    // Transfers
    if (!showTransfers && item.category === 'Trasferimento') {
      return false;
    }

    // NON filtriamo per tempo (ignora filtri temporali)

    return true;
  });
}
// Calcola indici finanziari avanzati
export function getAdvancedMetrics(periodData, allExpenses, allIncomes, netWorth) {
  const { totalExpenses, totalIncomes, balance } = getTotal(periodData.expenses, periodData.incomes);
  
  // 1. Tasso di Risparmio (%)
  const savingsRate = totalIncomes > 0 ? (balance / totalIncomes) * 100 : 0;

  // 2. Autonomia Finanziaria (mesi di sopravvivenza)
  const monthlyData = getMonthlyData(allExpenses, allIncomes);
  const totalMonths = monthlyData.length || 1;
  const avgMonthlyExpense = monthlyData.reduce((sum, m) => sum + m.Spese, 0) / totalMonths;
  const runway = avgMonthlyExpense > 0 ? netWorth / avgMonthlyExpense : 0;

  // 3. Volatilità delle Spese (Coefficiente di variazione)
  let volatility = 0;
  if (monthlyData.length > 1) {
    const mean = avgMonthlyExpense;
    const variance = monthlyData.reduce((sum, m) => sum + Math.pow(m.Spese - mean, 2), 0) / monthlyData.length;
    const stdDev = Math.sqrt(variance);
    volatility = mean > 0 ? (stdDev / mean) * 100 : 0;
  }

  // 4. Punteggio di Salute Finanziaria (0-100)
  // Componenti:
  // - Tasso di Risparmio: > 20% è buono (40 pt)
  // - Autonomia: > 6 mesi è buono (30 pt)
  // - Volatilità: < 20% è buono (30 pt)
  let score = 0;
  score += Math.min(Math.max(savingsRate, 0) * 2, 40); // 20% savings = 40 pts
  score += Math.min(runway * 5, 30); // 6 months = 30 pts
  score += Math.max(0, 30 - (volatility / 2)); // 0% volatility = 30 pts, 60% = 0 pts

  return {
    savingsRate: toFixedNumber(savingsRate),
    runway: toFixedNumber(runway),
    volatility: toFixedNumber(volatility),
    healthScore: Math.round(score),
    avgMonthlyExpense: toFixedNumber(avgMonthlyExpense)
  };
}

// Prepara i dati per il Radar Chart (confronto intensità per categoria)
export function getRadarData(currentItems, previousItems) {
  const currentByCategory = getByCategory(currentItems);
  const previousByCategory = getByCategory(previousItems);
  
  // Ordina il periodo attuale per valore e prendi i primi 6
  const categories = Object.keys(currentByCategory)
    .sort((a, b) => currentByCategory[b] - currentByCategory[a])
    .slice(0, 6);

  return categories.map(cat => {
    const current = currentByCategory[cat] || 0;
    const previous = previousByCategory[cat] || 0;
    
    // Normalizza per il radar chart (0-100)
    const maxVal = Math.max(current, previous, 1);
    
    return {
      subject: cat,
      Attuale: toFixedNumber(current),
      Precedente: toFixedNumber(previous),
      fullMark: toFixedNumber(maxVal)
    };
  });
}
// Calcola i 7 indici finanziari richiesti
export function getFinancialIndices(allExpenses, allIncomes, wallets, debts) {
  // 1. Dati base - Riferimento all'ultimo 1 del mese
  const now = getCurrentRomeTime();
  const refDate = startOfMonth(now);
  const sixMonthsAgo = subMonths(refDate, 6);
  
  // Filtriamo i dati per il periodo di riferimento (ultimi 6 mesi completi prima dell'attuale)
  const periodExpenses = allExpenses?.filter(e => {
    const d = new Date(e.date);
    return d >= sixMonthsAgo && d < refDate && e.category !== 'Trasferimento';
  }) || [];
  
  const periodIncomes = allIncomes?.filter(i => {
    const d = new Date(i.date);
    return d >= sixMonthsAgo && d < refDate && i.category !== 'Trasferimento';
  }) || [];

  // Spese medie mensili nel periodo di 6 mesi (per Solidità e Controllo Spese)
  const monthlyExpenses = getMonthlyData(periodExpenses, []);
  const monthsInPeriodCount = Math.max(monthlyExpenses.length, 1);
  const avgMonthlyExpensePeriod = monthlyExpenses.reduce((sum, m) => sum + m.Spese, 0) / monthsInPeriodCount;

  // Liquidità (Contante + Conto Bancario + Altro non investimento)
  const liquidity = wallets
    ?.filter(w => !['Azioni', 'Obbligazioni', 'ETF'].includes(w.type))
    .reduce((sum, w) => sum + safeParseFloat(w.balance), 0) || 0;

  // Investimenti (Azioni, Obbligazioni, ETF)
  const investments = wallets
    ?.filter(w => ['Azioni', 'Obbligazioni', 'ETF'].includes(w.type))
    .reduce((sum, w) => sum + safeParseFloat(w.balance), 0) || 0;

  // Patrimonio Totale (Somma di tutti i conti)
  const totalAssets = wallets?.reduce((sum, w) => sum + safeParseFloat(w.balance), 0) || 0;

  // Calcolo Debiti e Crediti
  const personBalances = {};
  if (Array.isArray(debts)) {
    debts.forEach((debt) => {
      const personName = debt?.personName;
      const amount = safeParseFloat(debt?.amount);
      if (!personName || isNaN(amount)) return;
      const delta = debt.type === 'credit' ? amount : -amount;
      personBalances[personName] = (personBalances[personName] || 0) + delta;
    });
  }

  const creditTotal = Object.values(personBalances).filter(v => v > 0.01).reduce((sum, v) => sum + v, 0);
  const debtTotal = Object.values(personBalances).filter(v => v < -0.01).reduce((sum, v) => sum + Math.abs(v), 0);
  const netWorth = totalAssets + creditTotal - debtTotal;

  // 1. Indice di Solidità (Emergency Fund)
  // Target: 6 mesi di spese coperte = 100 punti
  const soliditaMonths = avgMonthlyExpensePeriod > 0 ? liquidity / avgMonthlyExpensePeriod : (liquidity > 0 ? 99 : 0);
  const soliditaScore = Math.min(Math.max((soliditaMonths / 6) * 100, 0), 100);

  // 2. Indice di Investimenti
  const crescitaPct = netWorth > 0 ? (investments / netWorth) : 0;
  const investimentiScore = Math.min(Math.max((crescitaPct / 0.6) * 100, 0), 100);

  // 3. Indice di Risparmio (negli ultimi 6 mesi)
  const totalPeriodIncome = periodIncomes.reduce((sum, i) => sum + safeParseFloat(i.amount), 0);
  const totalPeriodExpense = periodExpenses.reduce((sum, e) => sum + safeParseFloat(e.amount), 0);
  const periodBalance = totalPeriodIncome - totalPeriodExpense;
  const risparmioPct = totalPeriodIncome > 0 ? (periodBalance / totalPeriodIncome) : 0;
  const risparmioScore = Math.min(Math.max((risparmioPct / 0.4) * 100, 0), 100);

  // 4. Diversificazione Entrate (Nuovo Indice)
  const monthlyIncomesByStore = {};
  const last6MonthsKeys = [];
  for (let i = 5; i >= 0; i--) {
    const d = subMonths(refDate, i + 1);
    last6MonthsKeys.push(format(d, 'yyyy-MM'));
  }

  periodIncomes.forEach(inc => {
    const d = new Date(inc.date);
    const monthKey = format(d, 'yyyy-MM');
    const store = inc.store || 'Altro';
    const amount = safeParseFloat(inc.amount);
    
    if (!monthlyIncomesByStore[monthKey]) monthlyIncomesByStore[monthKey] = {};
    monthlyIncomesByStore[monthKey][store] = (monthlyIncomesByStore[monthKey][store] || 0) + amount;
  });

  const monthlyIncomes = last6MonthsKeys.map(monthKey => {
    const stores = monthlyIncomesByStore[monthKey] || {};
    const total = Object.values(stores).reduce((sum, val) => sum + val, 0);
    const diversificationCount = Object.values(stores).filter(amount => amount >= 200).length;
    return { month: monthKey, Entrate: total, diversificationCount };
  });
  
  const avgDiversification = monthlyIncomes.reduce((sum, m) => sum + m.diversificationCount, 0) / 6;
  const avgMonthlyIncomePeriod = monthlyIncomes.reduce((sum, m) => sum + m.Entrate, 0) / 6;
  const diversificazioneScore = Math.min(Math.max((avgDiversification / 4) * 100, 0), 100);

  // 5. Controllo Spese (Oscillazione Spese negli ultimi 6 mesi)
  let expenseVolatility = 0;
  if (monthlyExpenses.length > 1) {
    const mean = avgMonthlyExpensePeriod;
    const variance = monthlyExpenses.reduce((sum, m) => sum + Math.pow(m.Spese - mean, 2), 0) / monthlyExpenses.length;
    expenseVolatility = mean > 0 ? (Math.sqrt(variance) / mean) : 0;
  }
  const controlloSpeseScore = Math.min(Math.max(((1 - expenseVolatility) / 0.90) * 100, 0), 100);

  // 6. Posizionamento Reddito (Media ultimi 6 mesi completi)
  const incomeScore = Math.min(Math.max(avgMonthlyIncomePeriod / 30, 0), 100);

  // 7. Indice di Crescita (Trend Entrate ultimi 6 mesi)
  let growthScore = 50; 
  let growthSlopePct = 0;
  if (monthlyIncomes.length >= 2) {
    const n = monthlyIncomes.length;
    const sumX = (n * (n - 1)) / 2;
    const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;
    let sumY = 0;
    let sumXY = 0;
    monthlyIncomes.forEach((m, i) => {
      sumY += m.Entrate;
      sumXY += i * m.Entrate;
    });
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - Math.pow(sumX, 2));
    const avgY = sumY / n;
    if (avgY > 0) {
      growthSlopePct = (slope / avgY);
      growthScore = 50 + (growthSlopePct * 500);
    }
  }
  growthScore = Math.min(Math.max(growthScore, 0), 100);

  // 8. Solvibilità
  const ratioDebitoAssets = totalAssets > 0 ? (debtTotal / totalAssets) : (debtTotal > 0 ? 1.5 : 0);
  const solvibilitaScore = Math.min(Math.max(((1.5 - ratioDebitoAssets) / 1.3) * 100, 0), 100);

  const getEvaluationByScore = (score, config) => {
    for (const range of config) {
      if (score <= range.max) return range;
    }
    return config[config.length - 1];
  };

  return {
    solidita: {
      score: Math.round(soliditaScore),
      value: `${soliditaMonths.toFixed(1)} mesi`,
      ...getEvaluationByScore(soliditaScore, [
        { max: 13, label: 'NULLO', comment: 'Sei scoperto.', color: '#ef4444' },
        { max: 26, label: 'BASSO', comment: 'Copertura minima.', color: '#f97316' },
        { max: 50, label: 'MEDIO', comment: 'Inizia la sicurezza.', color: '#eab308' },
        { max: 75, label: 'BUONO', comment: 'Dormi sonni tranquilli.', color: '#84cc16' },
        { max: 100, label: 'OTTIMO', comment: 'Corazzato contro gli imprevisti.', color: '#22c55e' }
      ]),
      details: `Liquidità (${liquidity.toFixed(0)}€) / Spese medie mensili (${avgMonthlyExpensePeriod.toFixed(0)}€). Obiettivo: 6 mesi. Esclusi asset volatili.`
    },
    investimenti: {
      score: Math.round(investimentiScore),
      value: `${(crescitaPct * 100).toFixed(1)}%`,
      ...getEvaluationByScore(investimentiScore, [
        { max: 13, label: 'NULLO', comment: "L'inflazione ti mangia tutto.", color: '#ef4444' },
        { max: 26, label: 'BASSO', comment: 'Troppi contanti fermi.', color: '#f97316' },
        { max: 50, label: 'MEDIO', comment: 'Iniziando a seminare.', color: '#eab308' },
        { max: 75, label: 'BUONO', comment: 'Portafoglio bilanciato.', color: '#84cc16' },
        { max: 100, label: 'OTTIMO', comment: 'Macchina da crescita attiva.', color: '#22c55e' }
      ]),
      details: `Investimenti (${investments.toFixed(0)}€) / Patrimonio netto (${netWorth.toFixed(0)}€). Obiettivo: 60%.`
    },
    crescita: {
      score: Math.round(growthScore),
      value: `${(growthSlopePct * 100).toFixed(1)}%/m`,
      ...getEvaluationByScore(growthScore, [
        { max: 25, label: 'CALO', comment: 'Entrate in contrazione.', color: '#ef4444' },
        { max: 45, label: 'DEBOLE', comment: 'Trend leggermente negativo.', color: '#f97316' },
        { max: 55, label: 'STABILE', comment: 'Entrate costanti.', color: '#eab308' },
        { max: 80, label: 'IN CRESCITA', comment: 'Trend positivo.', color: '#84cc16' },
        { max: 100, label: 'BOOM', comment: 'Crescita accelerata.', color: '#22c55e' }
      ]),
      details: `Inclinazione del reddito negli ultimi 6 mesi. +10%/mese = 100pt, Piatto = 50pt.`
    },
    risparmio: {
      score: Math.round(risparmioScore),
      value: `${(risparmioPct * 100).toFixed(1)}%`,
      ...getEvaluationByScore(risparmioScore, [
        { max: 13, label: 'NULLO', comment: 'Risparmio sotto la media.', color: '#ef4444' },
        { max: 26, label: 'BASSO', comment: 'Leggermente sotto la media.', color: '#f97316' },
        { max: 50, label: 'MEDIO', comment: 'Risparmio nella media.', color: '#eab308' },
        { max: 75, label: 'BUONO', comment: 'Sopra la media.', color: '#84cc16' },
        { max: 100, label: 'OTTIMO', comment: 'Ben sopra la media.', color: '#22c55e' }
      ]),
      details: `(Entrate - Spese) / Entrate. Indica quanto riesci ad accantonare. Obiettivo: 40%.`
    },
    diversificazione: {
      score: Math.round(diversificazioneScore),
      value: `${avgDiversification.toFixed(1)} fonti`,
      ...getEvaluationByScore(diversificazioneScore, [
        { max: 25, label: 'NULLA', comment: 'Dipendi da una sola fonte.', color: '#ef4444' },
        { max: 50, label: 'BASSA', comment: 'Poca varietà nelle entrate.', color: '#f97316' },
        { max: 75, label: 'BUONA', comment: 'Entrate ben distribuite.', color: '#eab308' },
        { max: 100, label: 'ECCELLENTE', comment: 'Massima resilienza finanziaria.', color: '#22c55e' }
      ]),
      details: `Media fonti mensili >= 200€ negli ultimi 6 mesi. Obiettivo: 4 fonti = 100pt.`
    },
    controllo: {
      score: Math.round(controlloSpeseScore),
      value: `${(expenseVolatility * 100).toFixed(1)}% osc.`,
      ...getEvaluationByScore(controlloSpeseScore, [
        { max: 13, label: 'NULLO', comment: 'Uscite fuori controllo.', color: '#ef4444' },
        { max: 26, label: 'BASSO', comment: 'Spese molto irregolari.', color: '#f97316' },
        { max: 50, label: 'MEDIO', comment: 'Gestione accettabile.', color: '#eab308' },
        { max: 75, label: 'BUONO', comment: 'Spese ben pianificate.', color: '#84cc16' },
        { max: 100, label: 'OTTIMO', comment: 'Disciplina di spesa totale.', color: '#22c55e' }
      ]),
      details: `Costanza del costo della vita. Obiettivo: Oscillazione < 10%.`
    },
    reddito: {
      score: Math.round(incomeScore),
      value: `${avgMonthlyIncomePeriod.toFixed(0)}€/mese`,
      ...getEvaluationByScore(incomeScore, [
        { max: 13, label: 'NULLO', comment: 'Sotto la soglia minima.', color: '#ef4444' },
        { max: 26, label: 'BASSO', comment: 'Sotto la media nazionale.', color: '#f97316' },
        { max: 50, label: 'MEDIO', comment: 'In linea con la media.', color: '#eab308' },
        { max: 75, label: 'BUONO', comment: 'Reddito medio-alto.', color: '#84cc16' },
        { max: 100, label: 'OTTIMO', comment: 'Top 10% dei redditi.', color: '#22c55e' }
      ]),
      details: `Media ultimi 6 mesi completi. Obiettivo: 1500€=50, 3000€=100.`
    },
    solvibilita: {
      score: Math.round(solvibilitaScore),
      value: `${(ratioDebitoAssets * 100).toFixed(0)}% deb.`,
      ...getEvaluationByScore(solvibilitaScore, [
        { max: 13, label: 'NULLO', comment: 'Debito superiore ai risparmi.', color: '#ef4444' },
        { max: 26, label: 'BASSO', comment: 'Debito borderline.', color: '#f97316' },
        { max: 50, label: 'MEDIO', comment: 'Situazione sotto controllo.', color: '#eab308' },
        { max: 75, label: 'BUONO', comment: 'Ottima copertura del debito.', color: '#84cc16' },
        { max: 100, label: 'OTTIMO', comment: 'Sei libero da debiti.', color: '#22c55e' }
      ]),
      details: `Debito netto (${debtTotal.toFixed(0)}€) vs Patrimonio Conti (${totalAssets.toFixed(0)}€).`
    }
  };
}
// Calcola l'andamento di una tipologia di investimento (Azioni, Obbligazioni, ETF)
export function getInvestmentTrend(expenses, incomes, wallets, type, filterStart, filterEnd, categories) {
  if (!filterStart || !filterEnd) return [];

  const now = getCurrentRomeTime();
  
  // Helper per risolvere il nome della categoria dall'ID o dal nome stesso
  const resolveCategoryName = (idOrName) => {
    if (!idOrName) return '';
    const allCats = [
      ...(categories?.expense || []),
      ...(categories?.income || [])
    ];
    const found = allCats.find(c => String(c.id) === String(idOrName) || c.name === idOrName);
    return found ? found.name : String(idOrName);
  };
  
  // 1. Filtra i wallet di questa tipologia
  const targetWallets = (wallets || []).filter(w => w.type === type && !w?.isVirtual);
  const targetWalletIds = new Set(targetWallets.map(w => w.id));
  
  if (targetWalletIds.size === 0) return [];

  // 2. Raccoglie tutti i movimenti storici per questi wallet
  const allHistory = [];
  const pushTyped = (items, sign) => {
    (items || []).forEach(item => {
      if (targetWalletIds.has(item.walletId)) {
        allHistory.push({
          date: new Date(item.date),
          amount: safeParseFloat(item.amount) * sign,
          categoryName: resolveCategoryName(item.category),
          category: item.category
        });
      }
    });
  };
  pushTyped(incomes, 1);
  pushTyped(expenses, -1);

  if (allHistory.length === 0) return [];

  // Ordina per data crescente
  allHistory.sort((a, b) => a.date - b.date);
  
  // 3. Definisce l'intervallo temporale dal primo movimento ad oggi
  const firstTxDate = allHistory[0].date;
  const rangeStart = startOfMonth(firstTxDate);
  
  // Genera nodi al 1° di ogni mese fino ad oggi
  const monthNodes = eachMonthOfInterval({ 
    start: rangeStart, 
    end: now 
  });

  // Aggiunge anche il 1° del mese di oggi per riflettere il saldo al termine del mese scorso
  // (Rimosso il push del nodo "now" come richiesto dall'utente)
  
  // 4. Calcola il saldo cumulativo a ogni nodo
  const trendData = monthNodes.map((nodeDate) => {
    let total = 0;
    let capital = 0;

    allHistory.forEach(tx => {
      // Includiamo tutti i movimenti avvenuti entro il mese di nodeDate (ovvero prima del 1° del mese successivo)
      const nextMonthStart = startOfMonth(addMonths(nodeDate, 1));
      if (tx.date < nextMonthStart) {
        total += tx.amount;
        
        // Controllo robusto per verificare se la transazione è un trasferimento
        const isTransfer = 
          tx.category === 'Trasferimento' || 
          tx.categoryName?.toLowerCase().trim() === 'trasferimento' ||
          tx.categoryName?.toLowerCase().trim().includes('trasferiment');
          
        if (isTransfer) {
          capital += tx.amount;
        }
      }
    });

    const label = format(nodeDate, "MMM ''yy", { locale: it });

    return {
      date: nodeDate,
      label,
      total: toFixedNumber(total),
      capital: toFixedNumber(capital)
    };
  });

  // 5. Filtra i risultati in base ai filterStart/filterEnd delle statistiche
  // Usiamo filterEnd inclusivo perché corrisponde solitamente all'inizio del mese successivo
  return trendData.filter(d => d.date >= filterStart && d.date <= filterEnd);
}

